import React, { useCallback, useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
  UseFormSetError,
} from "react-hook-form";
import ToggleButton from "../../../components/ToggleButton";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import { OrderFormData, LocationState, AddressOption } from "../../../types/types";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
  useDistrictFetchData,
} from "../../../apis/locationApis";
import { IconUser } from "@tabler/icons-react";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import SenderAddress from "./SenderAddress";
import { useLanguage } from "../../../context/LanguageContext";
import toast from "react-hot-toast";

interface SenderSectionProps {
  clients: any[];
  register: UseFormRegister<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  clearErrors: UseFormClearErrors<OrderFormData>;
  watch: any;
  useDifferentSender: boolean;
  setUseDifferentSender: (value: boolean) => void;
  useFMOrder?: boolean;
  setUseFMOrder?: (value: boolean) => void;
  onClientSelect: (clientId: string) => void;
  control: Control<OrderFormData>;
  setError: UseFormSetError<OrderFormData>;
  isSidebar?: boolean;
}

// ── Staff-style section header ────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title, right }: { icon: any; title: string; right?: React.ReactNode }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center justify-between gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
          <Icon size={16} className="text-[#212121]" stroke={1.5} />
        </div>
        <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">{t(title)}</p>
      </div>
      {right}
    </div>
  );
};

const SenderSection: React.FC<SenderSectionProps> = ({
  clients,
  register,
  errors,
  setValue,
  clearErrors,
  watch,
  useDifferentSender,
  setUseDifferentSender,
  useFMOrder: _useFMOrder,
  setUseFMOrder: _setUseFMOrder,
  onClientSelect,
  control,
  setError,
  isSidebar = false,
}) => {
  const { t } = useLanguage();
  const [senderLocation, setSenderLocation] = React.useState<LocationState>({});
  const selectedClientId = watch("clientId");
  const selectedClient = clients?.find((c: any) => c?.id === selectedClientId);
  const isNippcoMerchant = selectedClient?.isNippco === true;

  const handleToggleSender = () => {
    if (isNippcoMerchant) {
      toast.error(t("For nippco merchant orders alternate address is mandatory"));
      return;
    }
    setUseDifferentSender(!useDifferentSender);
  };
  const [searchRef, setSearchRef] = useState<string>("");
  const [districtSearchRef, setDistrictSearchRef] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<AddressOption>("short");

  const countryWatch = watch("sender.country");
  const countryNameWatch = watch("sender.countryName");
  const cityWatch = watch("sender.city");
  const cityNameWatch = watch("sender.cityName");
  const districtWatch = watch("sender.district");
  const districtNameWatch = watch("sender.districtName");

  React.useEffect(() => {
    setSenderLocation({
      country: countryWatch ? { id: countryWatch, name: countryNameWatch || "" } : undefined,
      city: cityWatch ? { id: cityWatch, name: cityNameWatch || "" } : undefined,
      district: districtWatch ? { id: districtWatch, name: districtNameWatch || "" } : undefined,
    });
  }, [countryWatch, countryNameWatch, cityWatch, cityNameWatch, districtWatch, districtNameWatch]);

  const { data: countries = [] } = useFetchCountriesAllData();
  const { data: senderCities = [], refetch: fetchSenderCities, isPending: isCitiesFetchPending } =
    useFetchCountryCitiesAllData(senderLocation?.country?.id, searchRef, selectedOption);
  const { data: senderDistricts = [], refetch: fetchSenderDistricts, isPending: isDistrictFetchPending } =
    useDistrictFetchData(senderLocation?.city?.id, districtSearchRef, selectedOption);

  const handleLocationChange = (field: keyof LocationState, value: any) => {
    if (!value?.id) return;
    setSenderLocation((prev) => {
      const newLoc = { ...prev, [field]: value };
      if (field === "country") {
        newLoc.city = undefined;
        newLoc.district = undefined;
        setValue("sender.city", "");
        setValue("sender.cityName", "");
        setValue("sender.district", "");
        setValue("sender.districtName", "");
      } else if (field === "city") {
        newLoc.district = undefined;
        setValue("sender.district", "");
        setValue("sender.districtName", "");
      }
      return newLoc;
    });
    setValue(`sender.${field}` as any, value.id);
    setValue(`sender.${field}Name` as any, value.name);
    setValue("sender.dialCode", value?.dialCode);
    if (field === "country" && value.id) fetchSenderCities();
    else if (field === "city" && value.id) fetchSenderDistricts();
  };

  const handleCitySearch = (query: string) => {
    if (query?.trim().length >= 3) { setSearchRef(query); fetchSenderCities(); }
  };

  const handleCityChange = useCallback((val: string) => {
    const city = senderCities?.find((c: any) => c.id === val);
    if (!city) return;
    setValue("sender.city", city.id); setValue("sender.cityName", city.name);
    setSenderLocation((prev) => ({ ...prev, city, district: undefined }));
    setValue("sender.district", "");
    fetchSenderDistricts();
  }, [senderCities, setValue, fetchSenderDistricts]);

  const handleDistrictSearch = (query: string) => {
    if (query.trim().length >= 3) { setDistrictSearchRef(query); fetchSenderDistricts(); }
  };

  const handleDistrictChange = useCallback((val: string) => {
    const d = senderDistricts.find((x: any) => x.id === val);
    if (!d) return;
    setValue("sender.district", d.id); setValue("sender.districtName", d.name);
    setSenderLocation((prev) => ({ ...prev, district: d }));
  }, [senderDistricts, setValue]);

  if (isSidebar) {
    return (
      <div className="relative z-20 focus-within:z-40 group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <SectionHeader
          icon={IconUser}
          title="Sender Details"
          right={
            <div className="relative z-10 flex items-center gap-1.5">
              <ToggleButton onChange={handleToggleSender} checked={useDifferentSender} />
              <span className="text-xs text-slate-500">{t("Different address")}</span>
            </div>
          }
        />

        {!useDifferentSender && (
          <NewSelectInput
            name="clientId"
            label="Client"
            placeholder="Select client"
            className="flex flex-col gap-1"
            selectOptions={clients?.map((c) => ({ id: c?.id, value: c?.id, text: c?.name })) || []}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: "Client is required" }}
            error={errors.clientId}
            disabled={!clients?.length}
            value={watch("clientId")}
            setValue={onClientSelect}
          />
        )}

        {useDifferentSender && (
          <div className="space-y-3 relative z-10">
            <div className="grid grid-cols-2 gap-3">
              <NewSelectInput name="sender.country" label="Country" placeholder="Select country"
                className="flex flex-col gap-1"
                selectOptions={countries.map((c: any) => ({ id: c.id, value: c.id, text: c.name }))}
                register={register} clearErrors={clearErrors} validation={{ required: "Required" }}
                error={errors.sender?.country} disabled={!countries.length}
                value={senderLocation.country?.id}
                setValue={(val: string) => { const c = countries.find((x: any) => x.id === val); handleLocationChange("country", c); }} />

              <div className="space-y-1 relative">
                <label className="text-[#1E293B] text-sm whitespace-nowrap">
                  {t("Mobile")}<span className="text-red-600 ml-1">*</span>
                </label>
                <div className="w-full">
                  <div className={`flex items-center rounded-xl border bg-white px-4 py-2 ${errors.sender?.mobile ? "border-red-400" : "border-[#D8D8D8]"}`}>
                    <span className="text-gray-500 text-sm whitespace-nowrap border-r border-gray-200 pr-2 mr-2">
                      {watch("sender.dialCode") ? `+${watch("sender.dialCode")}` : "+--"}
                    </span>
                    <input type="number" placeholder={t("Mobile number")}
                      className="w-full outline-none bg-transparent text-sm text-[#1E1E1E] hide-spinner"
                      {...register("sender.mobile", {
                        required: "Required",
                        validate: (v: string) => { const tVal = v?.toString().trim(); return (tVal.length === 9 || tVal.length === 10) || "9-10 digits"; }
                      })} />
                  </div>
                  {errors.sender?.mobile && <FormErrorMessage error={errors.sender?.mobile} />}
                </div>
              </div>
            </div>

            <NewinputField name="sender.name" type="text" title="Name" register={register}
              error={errors.sender?.name} validation={{ required: "Required" }}
              className="flex flex-col gap-1" />

            <NewinputField name="sender.email" type="text" title="Email" register={register}
              error={errors.sender?.email}
              validation={{ required: "Required", setValueAs: (v: string) => v?.toLowerCase(), pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email" } }}
              className="flex flex-col gap-1" />

            <SenderAddress
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              control={control}
              setError={setError}
              cities={senderCities}
              handleCitySearch={handleCitySearch}
              isCitiesFetchPending={isCitiesFetchPending}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              countries={countries}
              onCountryChange={(countryId: string) => {
                const country = countries.find((c: any) => c.id === countryId);
                handleLocationChange("country", country);
              }}
              isSidebar={isSidebar}
            />
          </div>
        )}
      </div>
    );
  }

  // Non-sidebar original layout
  return (
    <div className="col-span-1">
      <div className="col-span-6 flex items-center space-x-4 justify-between">
        <p className="text-black font-medium text-base uppercase">{t("Sender Details")}</p>
        <div className="flex justify-start items-center gap-1">
          <ToggleButton onChange={handleToggleSender} checked={useDifferentSender} />
          <p className="text-[#1E293B] text-sm">{t("Send from different address")}</p>
        </div>
      </div>
      {!useDifferentSender && (
        <SelectInput className="col-span-2" name="clientId" label="Select Client" placeholder="Name"
          selectOptions={clients?.map((c) => ({ id: c?.id, value: c?.id, text: c?.name })) || []}
          clearErrors={clearErrors} register={register} validation={{ required: "Client is required" }}
          error={errors.clientId} disabled={!clients?.length} value={watch("clientId")} setValue={onClientSelect} />
      )}
      {useDifferentSender && (
        <div className="mt-4 grid grid-cols-6 gap-2">
          <SelectInput name="sender.country" className="col-span-6" label="Country" placeholder="Select Country"
            selectOptions={countries.map((c: any) => ({ id: c.id, value: c.id, text: c.name }))}
            register={register} clearErrors={clearErrors} validation={{ required: "Sender country is required" }}
            error={errors.sender?.country} disabled={!countries.length} value={senderLocation.country?.id}
            setValue={(val: string) => { const c = countries.find((x: any) => x.id === val); handleLocationChange("country", c); }} />
          <Input name="sender.name" type="text" title="Name" register={register} error={errors.sender?.name} validation={{ required: "Sender name is required" }} className="col-span-2" />
          <Input name="sender.email" type="text" title="Email" register={register} error={errors.sender?.email}
            validation={{ required: "Sender email is required", setValueAs: (v: string) => v?.toLowerCase(), pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email" } }} className="col-span-2" />
          <SenderAddress
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            clearErrors={clearErrors}
            control={control}
            setError={setError}
            className="col-span-6"
            cities={senderCities}
            handleCitySearch={handleCitySearch}
            isCitiesFetchPending={isCitiesFetchPending}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            countries={countries}
            onCountryChange={(countryId: string) => {
              const country = countries.find((c: any) => c.id === countryId);
              handleLocationChange("country", country);
            }}
            isSidebar={isSidebar}
          />
        </div>
      )}
    </div>
  );
};

export default SenderSection;
